import type { JSX, FormEvent } from "react";
import { useEffect, useState } from "react";
import { ulid } from "ulid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../auth/AuthProvider.js";
import { friendlyError } from "../../shared/http.js";
import {
  getMembers,
  createInvite,
  revokeInvite,
  removeMember,
  patchHousehold,
  type MembersResponse,
} from "./api.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui/form.js";
import { Alert, AlertDescription } from "@/ui/alert.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog.js";

const inviteSchema = z.object({
  email: z.string().email("Enter a valid email"),
});
type InviteInput = z.infer<typeof inviteSchema>;

const renameSchema = z.object({
  name: z.string().min(1).max(80).refine((s) => s.trim().length > 0, "Required"),
});
type RenameInput = z.infer<typeof renameSchema>;

// A handful of common IANA zones is enough for v1 — good enough for a household to pick
// the one it actually lives in. Order matters only for the default (first = initial value).
const COMMON_TIMEZONES = [
  "America/Toronto",
  "America/Vancouver",
  "America/New_York",
  "Europe/London",
  "UTC",
];

interface PendingRemoval {
  userId: string;
  displayName: string;
}

interface PendingRevoke {
  inviteId: string;
  email: string;
}

export function HouseholdSettings(): JSX.Element {
  const auth = useAuth();
  const { me } = auth;
  const hhId = me?.user.currentHouseholdId ?? "";
  const myUserId = me?.user.userId ?? "";
  const myRole =
    me?.memberships.find((m) => m.householdId === hhId)?.role ?? "member";
  const [data, setData] = useState<MembersResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<PendingRevoke | null>(null);
  // The synced value from `/me`'s membership row (Task 5) — kept in sync via the
  // effect below so a later `refresh()` (e.g. after a successful save, or a household
  // switch) updates the select without the user having to reload the page.
  const membershipTz = me?.memberships.find((m) => m.householdId === hhId)?.timezone;
  const [timezone, setTimezone] = useState<string>(membershipTz ?? COMMON_TIMEZONES[0]!);
  const [tzBusy, setTzBusy] = useState(false);

  useEffect(() => {
    if (membershipTz) setTimezone(membershipTz);
  }, [membershipTz]);

  const householdName =
    me?.memberships.find((m) => m.householdId === hhId)?.householdName ?? "";

  const form = useForm<InviteInput>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "" },
  });

  const renameForm = useForm<RenameInput>({
    resolver: zodResolver(renameSchema),
    mode: "onChange",
    defaultValues: { name: householdName },
  });

  // Keep the rename form in sync when the active household changes (or on first
  // load, since defaultValues capture only the initial render).
  useEffect(() => {
    renameForm.reset({ name: householdName });
  }, [householdName, renameForm]);

  async function submitRename(v: RenameInput): Promise<void> {
    if (!hhId) return;
    setBusy(true);
    try {
      const h = await patchHousehold(hhId, { name: v.name.trim() });
      toast.success(`Renamed to '${h.name}'`);
      renameForm.reset({ name: h.name });
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitTimezone(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!hhId) return;
    setTzBusy(true);
    try {
      const h = await patchHousehold(hhId, { timezone });
      setTimezone(h.timezone);
      toast.success(`Household timezone set to ${h.timezone}`);
      await auth.refresh();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setTzBusy(false);
    }
  }

  async function reload(): Promise<void> {
    if (!hhId) return;
    setData(await getMembers(hhId));
  }

  useEffect(() => {
    void reload();
  }, [hhId]);

  async function onInvite(values: InviteInput): Promise<void> {
    setErr(null);
    setBusy(true);
    try {
      const res = await createInvite(hhId, values.email.trim().toLowerCase(), ulid());
      form.reset();
      toast.success(`Invite sent to ${res.email}`);
      await reload();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirmRevoke(): Promise<void> {
    if (!pendingRevoke) return;
    const { inviteId, email } = pendingRevoke;
    setBusy(true);
    try {
      await revokeInvite(hhId, inviteId);
      toast.success(`Invite to ${email} revoked`);
      await reload();
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
      setPendingRevoke(null);
    }
  }

  async function confirmRemove(): Promise<void> {
    if (!pendingRemoval) return;
    const { userId, displayName } = pendingRemoval;
    setBusy(true);
    try {
      await removeMember(hhId, userId);
      toast.success(`Removed ${displayName}`);
      await reload();
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
      setPendingRemoval(null);
    }
  }

  if (!hhId) {
    return <p className="text-muted-foreground">No active household.</p>;
  }
  if (!data) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Household</h1>

      {/* Members */}
      <section className="pb-8 mb-2 border-b border-zinc-800">
        <h2 className="text-zinc-50 font-medium mb-3">Members</h2>
        <ul className="space-y-3">
          {data.members.map((m) => {
            const isMe = m.userId === myUserId;
            const initial = m.displayName[0]?.toUpperCase() ?? "?";
            const isOwnerRole = m.role === "owner";
            return (
              <li key={m.userId} className="flex items-center gap-3">
                <span
                  className={`w-9 h-9 rounded-full text-xs font-medium flex items-center justify-center shrink-0 ${
                    isOwnerRole
                      ? "bg-emerald-700 text-emerald-50"
                      : "bg-zinc-700 text-zinc-100"
                  }`}
                  aria-hidden
                >
                  {initial}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-100 text-sm font-medium truncate">
                    {m.displayName}
                    {isMe && <span className="ml-2 text-[0.625rem] uppercase tracking-wider text-zinc-500">you</span>}
                  </div>
                  <div className="text-zinc-500 text-xs truncate">{m.email}</div>
                </div>
                <span
                  className={`text-[0.6875rem] uppercase tracking-wider rounded-md px-2 py-0.5 shrink-0 ${
                    isOwnerRole
                      ? "text-emerald-400 bg-emerald-500/10"
                      : "text-zinc-400 bg-zinc-800"
                  }`}
                >
                  {m.role}
                </span>
                {myRole === "owner" && !isMe && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    aria-label={`Remove ${m.displayName}`}
                    onClick={() =>
                      setPendingRemoval({
                        userId: m.userId,
                        displayName: m.displayName,
                      })
                    }
                  >
                    Remove
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {/* Invite a member (+ pending invites inline) */}
      {myRole === "owner" && (
        <section className="pt-6 pb-8 mb-2 border-b border-zinc-800">
          <h2 className="text-zinc-50 font-medium mb-1">Invite a member</h2>
          <p className="text-zinc-500 text-xs mb-3">
            Invites are valid for 7 days from the date sent.
          </p>

          {data.pendingInvites.length > 0 && (
            <ul className="space-y-2 mb-4 max-w-md">
              {data.pendingInvites.map((inv) => (
                <li key={inv.inviteId} className="flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-100 truncate">{inv.email}</div>
                    <div className="text-zinc-500 text-xs">
                      Expires {new Date(inv.expiresAt).toLocaleString()}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      setPendingRevoke({ inviteId: inv.inviteId, email: inv.email })
                    }
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <Form {...form}>
            <form
              onSubmit={(e) => void form.handleSubmit(onInvite)(e)}
              className="flex flex-col sm:flex-row gap-2 max-w-md"
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="sr-only">Invite email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="partner@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {busy ? "Sending…" : "Send invite"}
              </Button>
            </form>
          </Form>

          {err && (
            <Alert variant="destructive" role="alert" className="mt-3 max-w-md">
              <AlertDescription>{err}</AlertDescription>
            </Alert>
          )}
        </section>
      )}

      {/* Rename */}
      {myRole === "owner" && (
        <section className="pt-6">
          <h2 className="text-zinc-50 font-medium mb-1">Household name</h2>
          <p className="text-zinc-500 text-xs mb-3">Visible to all members.</p>
          <Form {...renameForm}>
            <form
              onSubmit={(e) => { void renameForm.handleSubmit(submitRename)(e); }}
              className="flex flex-col sm:flex-row gap-2 max-w-md"
            >
              <FormField
                control={renameForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="sr-only">New household name</FormLabel>
                    <FormControl>
                      <Input placeholder="New name…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={!renameForm.formState.isValid || busy}>
                Rename
              </Button>
            </form>
          </Form>
        </section>
      )}

      {/* Household timezone */}
      {myRole === "owner" && (
        <section className="pt-6">
          <h2 className="text-zinc-50 font-medium mb-1">Household timezone</h2>
          <p className="text-zinc-500 text-xs mb-3">
            Used to schedule the daily meal-plan email.
          </p>
          <form
            onSubmit={(e) => { void submitTimezone(e); }}
            className="flex flex-col sm:flex-row gap-2 max-w-md"
          >
            <label htmlFor="household-timezone" className="sr-only">
              Household timezone
            </label>
            <select
              id="household-timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="flex-1 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
            >
              {!COMMON_TIMEZONES.includes(timezone) && (
                <option key={timezone} value={timezone}>
                  {timezone}
                </option>
              )}
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
            <Button type="submit" disabled={tzBusy}>
              {tzBusy ? "Saving…" : "Save timezone"}
            </Button>
          </form>
        </section>
      )}

      <Dialog open={pendingRemoval !== null} onOpenChange={(open) => !open && setPendingRemoval(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove member?</DialogTitle>
            <DialogDescription>
              {pendingRemoval
                ? `${pendingRemoval.displayName} will be signed out and lose access to this household.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingRemoval(null)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmRemove()}
              disabled={busy}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingRevoke !== null} onOpenChange={(open) => !open && setPendingRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke invite?</DialogTitle>
            <DialogDescription>
              {pendingRevoke
                ? `The invite to ${pendingRevoke.email} will be invalidated. The recipient won't be able to join using the existing link.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingRevoke(null)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmRevoke()}
              disabled={busy}
            >
              Revoke invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
